"use client";

/**
 * Простой toast без сторонних либ. Глобальный контейнер слушает событие
 * `frogstat:toast` и показывает уведомление 3-4 секунды.
 *
 * Использование из любого места:
 *   import { showToast } from "@/app/components/Toast";
 *   showToast("Шаблон создан");
 *   showToast("Ошибка", "error");
 *
 * Тост с кнопкой действия (например «Отменить» у отменяемого удаления):
 *   showActionToast("Задача удалена", { label: "Отменить", onAction: ... }, { durationMs: 5000 });
 * Для отменяемых удалений есть готовый помощник — app/components/undoable-delete.ts.
 */

import { useCallback, useEffect, useRef, useState } from "react";

type ToastKind = "success" | "error" | "info";
interface ToastPayload {
  id: number;
  text: string;
  kind: ToastKind;
  closing?: boolean;
  /** Кнопка действия в тосте. CustomEvent в пределах окна передаёт колбэк по ссылке. */
  action?: { label: string; onAction: () => void };
  /** Своё время жизни тоста, мс (по умолчанию 3500). */
  durationMs?: number;
}

const EVENT_NAME = "frogstat:toast";
let seq = 0;

export function showToast(text: string, kind: ToastKind = "success"): void {
  if (typeof window === "undefined") return;
  const payload: ToastPayload = { id: ++seq, text, kind };
  window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: payload }));
}

/** Тост с кнопкой действия (клик по кнопке закрывает тост). */
export function showActionToast(text: string, action: { label: string; onAction: () => void }, opts?: { kind?: ToastKind; durationMs?: number }): void {
  if (typeof window === "undefined") return;
  const payload: ToastPayload = { id: ++seq, text, kind: opts?.kind ?? "info", action, durationMs: opts?.durationMs };
  window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: payload }));
}

export default function ToastContainer() {
  const [toasts, setToasts] = useState<ToastPayload[]>([]);
  // Храним активные таймеры чтобы очистить при unmount или manual dismiss.
  const timersRef = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());

  const dismiss = useCallback((id: number) => {
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
    setToasts(prev => prev.map(p => p.id === id ? { ...p, closing: true } : p));
    window.setTimeout(() => {
      setToasts(prev => prev.filter(p => p.id !== id));
    }, 120);
  }, []);

  useEffect(() => {
    const handler = (e: Event) => {
      const ev = e as CustomEvent<ToastPayload>;
      const t = ev.detail;
      if (!t) return;
      setToasts(prev => [...prev, t]);
      // Автоскрытие (по умолчанию 3.5 сек). Таймер регистрируем в map'е чтобы
      // можно было отменить при ручном закрытии (dismiss) или unmount.
      const timer = setTimeout(() => {
        dismiss(t.id);
      }, t.durationMs ?? 3500);
      timersRef.current.set(t.id, timer);
    };
    window.addEventListener(EVENT_NAME, handler);
    // Snapshot ссылки, чтобы в cleanup использовать именно то значение
    // которое было на момент монтирования — избегаем ESLint warning
    // «ref value will likely have changed by the time cleanup function runs».
    const timers = timersRef.current;
    return () => {
      window.removeEventListener(EVENT_NAME, handler);
      for (const timer of timers.values()) clearTimeout(timer);
      timers.clear();
    };
  }, [dismiss]);

  if (toasts.length === 0) return null;

  return (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[10050] flex flex-col gap-2 pointer-events-none">
      {toasts.map(t => (
        <div
          key={t.id}
          data-state={t.closing ? "closing" : "open"}
          className={`ui-toast pointer-events-auto flex max-w-[560px] items-center gap-3 pl-4 pr-2 py-2.5 rounded-xl shadow-lg text-sm font-medium ${
            t.kind === "success" ? "bg-green-600 text-white" :
            t.kind === "error"   ? "bg-red-600 text-white"   :
                                   "bg-gray-900 text-white"
          }`}
        >
          <span className="whitespace-pre-line">{t.text}</span>
          {t.action && (
            <button
              onClick={() => { t.action!.onAction(); dismiss(t.id); }}
              className="shrink-0 rounded-lg border border-white/25 px-2.5 py-1 text-[13px] font-bold text-white hover:bg-white/10 transition-colors"
            >
              {t.action.label}
            </button>
          )}
          <button
            onClick={() => dismiss(t.id)}
            className="w-6 h-6 flex items-center justify-center rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors text-base leading-none"
            aria-label="Закрыть"
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
}
